import React from 'react';

import { Modal } from 'src/components/Modal/Modal';
import { convertDate, formatFieldString, shortenString } from 'src/components/Table/helpers';

import {
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
} from 'src/pages/Transactions/styled';
import CopyableValue from 'src/pages/Transactions/CopyableValue';

type WaitingListDetailModalProps = {
  user: Record<string, any>;
  onClose: () => void;
};

const SectionSpacer = () => <div style={{ height: '1rem' }} />;

const pick = (...values: unknown[]) =>
  values.find((v) => v !== undefined && v !== null && v !== '') as string | number | undefined;


const WaitingListDetailModal: React.FC<WaitingListDetailModalProps> = ({ user, onClose }) => {
  const id = String(pick(user.id, user._id) ?? '');
  const name = user.name ? String(user.name) : '—';
  const email = user.email ? String(user.email) : '';
  const status = user.status ? String(user.status) : '';
  const country = user.country_code ? String(user.country_code) : '';
  const city = user.city ? String(user.city) : '';
  const street = user.street ? String(user.street) : '';
  const pin = user.pin_code ? String(user.pin_code) : '';
  const createdAt = user.created_at as string | undefined;

  const hasAddress = Boolean(country || city || street || pin);
  const createdMeta = createdAt
    ? `Joined ${convertDate(createdAt).date}${
        convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''
      }`
    : null;

  return (
    <Modal isOpen onClose={onClose}>
      <Breadcrumb>Waiting list{id ? ` / ${shortenString(id)}` : ''}</Breadcrumb>

      <HeadlineRow>
        <TitleGroup>
          <Title>
            {name !== '—' ? name : email || 'Waiting list user'}
            {status ? (
              <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
            ) : null}
          </Title>
          {createdMeta ? <MetaLine>{createdMeta}</MetaLine> : null}
        </TitleGroup>
      </HeadlineRow>

      <Card>
        <CardTitle>User</CardTitle>
        <DetailGrid>
          {id ? (
            <DetailItem>
              <DetailLabel>ID</DetailLabel>
              <CopyableValue value={id} label="User ID" />
            </DetailItem>
          ) : null}
          <DetailItem>
            <DetailLabel>Name</DetailLabel>
            <DetailValue $muted={name === '—'}>{name}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Email</DetailLabel>
            <DetailValue $muted={!email}>{email || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Email status</DetailLabel>
            <DetailValue>
              {status ? (
                <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
              ) : (
                '—'
              )}
            </DetailValue>
          </DetailItem>
          {createdAt ? (
            <DetailItem>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>
                {convertDate(createdAt).date}
                {convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''}
              </DetailValue>
            </DetailItem>
          ) : null}
        </DetailGrid>
      </Card>

      {hasAddress ? (
        <>
          <SectionSpacer />
          <Card>
            <CardTitle>Address</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Country</DetailLabel>
                <DetailValue $muted={!country}>{country || '—'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>City</DetailLabel>
                <DetailValue $muted={!city}>{city || '—'}</DetailValue>
              </DetailItem>
              {street ? (
                <DetailItem>
                  <DetailLabel>Street</DetailLabel>
                  <DetailValue>{street}</DetailValue>
                </DetailItem>
              ) : null}
              <DetailItem>
                <DetailLabel>Pin code</DetailLabel>
                <DetailValue $muted={!pin}>{pin || '—'}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>
        </>
      ) : null}
    </Modal>
  );
};

export default WaitingListDetailModal;
